import React, { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { RadioTower, Sparkles } from 'lucide-react';
import { DashboardLayout } from '../components/dashboard';
import { Button, Page, PageHeader, SegmentedControl, Spinner, StatGrid, Tabs } from '../components/ui';
import { useAuth } from '../contexts/AuthContext';
import { asOneOf, useUrlState, useUrlWriter } from '../hooks/useUrlState';
import type { AlertStatus, ScanRunSummary, SubjectRef, WatchSubjectType } from '../types/monitoring';
import { AlertsPanel } from '../features/monitoring/components/AlertsPanel';
import { DigestDialog, DigestDays } from '../features/monitoring/components/DigestDialog';
import { SignalFeed } from '../features/monitoring/components/SignalFeed';
import { SignalFiltersBar } from '../features/monitoring/components/SignalFilters';
import { Notice } from '../features/monitoring/components/ui';
import { Watchlist } from '../features/monitoring/components/Watchlist';
import { CATEGORIES, SEVERITIES, timeAgo, timeUntil } from '../features/monitoring/format';
import {
  SignalFilters,
  useAlerts,
  useOverview,
  useSignalQuery,
  useSignals,
  useWatches,
} from '../features/monitoring/useMonitoring';

const TABS = ['alerts', 'signals', 'watchlist'] as const;
type Tab = (typeof TABS)[number];
type AlertFilter = AlertStatus | 'all';

const ALERT_FILTERS: Array<{ value: AlertFilter; label: string }> = [
  { value: 'open', label: 'Open' },
  { value: 'acknowledged', label: 'Acknowledged' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'all', label: 'All' },
];

/* One codec per parameter, made once: the URL state reads them on every render */
const TAB = asOneOf(TABS);
const ALERT_STATUS = asOneOf(ALERT_FILTERS.map((option) => option.value));
const SUBJECT_TYPE = asOneOf<WatchSubjectType | ''>(['stakeholder', 'organization']);
/* Alerts already own `status`, so a signal's status answers to its own name */
const SIGNAL_STATUS = asOneOf<NonNullable<SignalFilters['status']>>(['open', 'new', 'reviewed', 'escalated', 'dismissed']);
const CATEGORY = asOneOf<NonNullable<SignalFilters['category']>>(CATEGORIES);
const SEVERITY = asOneOf<NonNullable<SignalFilters['severity']>>(SEVERITIES);
const DAYS = asOneOf<SignalFilters['days']>(['1', '7', '30', '90']);

/** What one full scan run did, in one line */
export const runSummary = (summary: ScanRunSummary): string =>
  summary.skippedReason ||
  `Scanned ${summary.scanned} · ${summary.stored} new signal${summary.stored === 1 ? '' : 's'} · ${
    summary.alertsRaised
  } alert${summary.alertsRaised === 1 ? '' : 's'}${summary.failures ? ` · ${summary.failures} failed` : ''}`;

export const MonitoringPage: React.FC = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  /*
   * The section and every filter are in the address bar. The alert badge and
   * the entity panels already link straight into a view this way, and now a
   * link back out of one shows the same thing.
   */
  const [tab, setTab] = useUrlState<Tab>('tab', 'alerts', TAB);
  const [alertFilter, setAlertFilter] = useUrlState<AlertFilter>('status', 'open', ALERT_STATUS);
  const [subjectId] = useUrlState('subjectId', '');
  const [subjectType] = useUrlState<WatchSubjectType | ''>('subjectType', '', SUBJECT_TYPE);
  const [subjectLabel] = useUrlState('subjectLabel', '');
  const [signalStatus] = useUrlState<NonNullable<SignalFilters['status']>>('signalStatus', '', SIGNAL_STATUS);
  const [category] = useUrlState<NonNullable<SignalFilters['category']>>('category', '', CATEGORY);
  const [severity] = useUrlState<NonNullable<SignalFilters['severity']>>('severity', '', SEVERITY);
  const [days] = useUrlState<SignalFilters['days']>('days', '', DAYS);
  const [search] = useUrlState('q', '');

  const filters = useMemo<SignalFilters>(
    () => ({
      // An entity arrives as three parameters because the label is only there
      // to name it before the watchlist has loaded
      subject:
        subjectId && subjectType ? { type: subjectType, id: subjectId, label: subjectLabel || 'Selected entity' } : null,
      status: signalStatus,
      category,
      severity,
      days,
      search,
    }),
    [subjectId, subjectType, subjectLabel, signalStatus, category, severity, days, search],
  );

  /* The bar hands back a whole set of filters; they move as one change */
  const write = useUrlWriter();
  const setFilters = useCallback(
    (next: SignalFilters) => {
      write({
        subjectId: next.subject?.id ?? null,
        subjectType: next.subject?.type ?? null,
        subjectLabel: next.subject?.label ?? null,
        signalStatus: next.status || null,
        category: next.category || null,
        severity: next.severity || null,
        days: next.days || null,
        q: next.search || null,
      });
    },
    [write],
  );

  const [digestOpen, setDigestOpen] = useState(false);
  const [digestDays, setDigestDays] = useState<DigestDays>('7');
  const [runResult, setRunResult] = useState<ScanRunSummary | null>(null);

  const overview = useOverview();
  // The evidence is the point of an alert, so it is fetched with the list
  const alerts = useAlerts(
    useMemo(
      () => ({ includeSignals: true, ...(alertFilter === 'all' ? {} : { status: alertFilter }) }),
      [alertFilter],
    ),
  );
  const signalQuery = useSignalQuery(filters);
  const signals = useSignals(signalQuery);
  const watches = useWatches();

  const subjects = useMemo(() => watches.watches.map((watch) => watch.subject), [watches.watches]);
  const resolveSubject = useCallback((id: string): SubjectRef | undefined => subjects.find((s) => s.id === id), [subjects]);

  const unavailable = overview.unavailable || alerts.unavailable || signals.unavailable || watches.unavailable;
  const unavailableNotice = (
    <Notice tone="quiet">Monitoring is not available on this server yet — the backend needs to be redeployed.</Notice>
  );

  /** A failure an analyst can act on: what went wrong, and a way to try again */
  const failureNotice = (message: string, retry: () => void) => (
    <Notice
      tone="error"
      action={
        <button
          type="button"
          onClick={retry}
          className="text-[12px] underline decoration-current/40 underline-offset-2 hover:decoration-current"
        >
          Try again
        </button>
      }
    >
      {message}
    </Notice>
  );

  const stats = overview.overview;
  const scan = stats?.scan;
  const scanRunning = !!scan?.running;
  // Say what is running, not just that something is
  const scanLabel = !scan?.running
    ? ''
    : scan.scope === 'watch' && scan.subject
      ? `Scanning ${scan.subject.label}…`
      : scan.total && scan.total > 1
        ? `Scanning ${scan.done + 1} of ${scan.total}…`
        : 'Scanning…';


  return (
    <DashboardLayout>
      <Page>
        <PageHeader
          title="Monitoring"
          meta={
            stats
              ? `${stats.watches.total} watched · last scan ${stats.lastScanAt ? timeAgo(stats.lastScanAt) : 'never'}`
              : undefined
          }
          actions={
            <>
              {scanRunning && (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-neutral-100 px-2.5 py-1 text-[12px] text-neutral-600">
                  <Spinner className="h-3 w-3 text-neutral-400" />
                  {scanLabel}
                </span>
              )}
              {isAdmin && (
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={unavailable || scanRunning || watches.busyId === 'all'}
                  title={scanRunning ? 'A scan is already running' : undefined}
                  loading={watches.busyId === 'all'}
                  onClick={async () => {
                    const summary = await watches.scanAll();
                    if (summary) {
                      setRunResult(summary);
                      overview.reload();
                      alerts.reload();
                    }
                  }}
                >
                  <RadioTower className="h-4 w-4" strokeWidth={1.75} />
                  Scan now
                </Button>
              )}
              <Button size="sm" variant="primary" disabled={unavailable} onClick={() => setDigestOpen(true)}>
                <Sparkles className="h-4 w-4" strokeWidth={1.75} />
                What changed
              </Button>
            </>
          }
        />

        {unavailable ? (
          <div className="mb-5">{unavailableNotice}</div>
        ) : (
          <>
            {stats && !stats.researchReady && (
              <Notice
                className="mb-4"
                tone="quiet"
                action={
                  isAdmin ? (
                    <Link
                      to="/ai-settings"
                      className="text-[12px] text-ink underline decoration-neutral-300 underline-offset-2 hover:decoration-ink"
                    >
                      AI settings
                    </Link>
                  ) : undefined
                }
              >
                No AI provider is set up for research, so scans will not run.
              </Notice>
            )}

            {runResult && (
              <Notice className="mb-4" tone={runResult.failures || runResult.skippedReason ? 'error' : 'quiet'}>
                {runSummary(runResult)}
              </Notice>
            )}

            {stats && (
              <StatGrid
                className="mb-6"
                items={[
                  { label: 'Open alerts', value: stats.alerts.open, hint: `${stats.alerts.bySeverity.critical || 0} critical` },
                  { label: 'Signals, 7 days', value: stats.signals.last7Days, hint: `${stats.signals.new} unreviewed` },
                  {
                    label: 'Watched',
                    value: stats.watches.total,
                    hint: `${stats.watches.daily} daily · ${stats.watches.weekly} weekly`,
                  },
                  {
                    label: 'Next scan',
                    value: stats.nextScanAt ? timeUntil(stats.nextScanAt).replace(/^in /, '') : '—',
                    hint: stats.lastScanAt ? `last ${timeAgo(stats.lastScanAt)}` : 'never scanned',
                  },
                ]}
              />
            )}
          </>
        )}

        <Tabs<Tab>
          label="Monitoring sections"
          className="mb-4"
          value={tab}
          onChange={setTab}
          items={[
            { value: 'alerts', label: 'Alerts', count: stats?.alerts.open },
            { value: 'signals', label: 'Signals', count: signals.total || undefined },
            { value: 'watchlist', label: 'Watchlist', count: watches.watches.length || undefined },
          ]}
        />

        {tab === 'alerts' && (
          <>
            <div className="mb-3">
              <SegmentedControl<AlertFilter>
                label="Alert status"
                value={alertFilter}
                onChange={setAlertFilter}
                items={ALERT_FILTERS}
              />
            </div>
            <AlertsPanel
              alerts={alerts.alerts}
              loading={alerts.loading}
              busyId={alerts.busyId}
              onStatus={async (id, status) => {
                await alerts.setStatus(id, status);
                overview.reload();
              }}
              notice={
                alerts.unavailable ? unavailableNotice : alerts.error ? failureNotice(alerts.error, alerts.reload) : undefined
              }
              emptyHint={
                alertFilter === 'open' ? 'No open alerts — nothing needs your attention' : 'Nothing here'
              }
            />
          </>
        )}

        {tab === 'signals' && (
          <>
            {!signals.unavailable && <SignalFiltersBar className="mb-3" filters={filters} onChange={setFilters} />}
            <SignalFeed
              signals={signals.items}
              loading={signals.loading}
              loadingMore={signals.loadingMore}
              hasMore={signals.hasMore}
              busyId={signals.busyId}
              onStatus={signals.setStatus}
              onLoadMore={signals.loadMore}
              notice={
                signals.unavailable ? unavailableNotice : signals.error ? failureNotice(signals.error, signals.reload) : undefined
              }
              emptyTitle={
                watches.watches.length === 0
                  ? 'Nothing is being watched yet — add an entity in the watchlist'
                  : 'No signals match these filters'
              }
              emptyAction={
                watches.watches.length === 0 ? (
                  <Button size="sm" variant="secondary" onClick={() => setTab('watchlist')}>
                    Open watchlist
                  </Button>
                ) : undefined
              }
            />
          </>
        )}

        {tab === 'watchlist' && (
          <Watchlist
            watches={watches.watches}
            loading={watches.loading}
            busyId={watches.busyId}
            canScan={isAdmin}
            onAdd={(subject) => watches.create({ subjectType: subject.type, subjectId: subject.id })}
            onUpdate={watches.update}
            onRemove={watches.remove}
            scanRunning={scanRunning}
            onScan={async (id) => {
              const result = await watches.scan(id);
              if (result) {
                overview.reload();
                if (result.alertsRaised) alerts.reload();
              }
              return result;
            }}
            notice={
              watches.unavailable ? unavailableNotice : watches.error ? failureNotice(watches.error, watches.reload) : undefined
            }
          />
        )}
      </Page>

      <DigestDialog
        open={digestOpen}
        onClose={() => setDigestOpen(false)}
        days={digestDays}
        onDaysChange={setDigestDays}
        resolveSubject={resolveSubject}
      />
    </DashboardLayout>
  );
};

export default MonitoringPage;
